// src/components/ChecklistRow.tsx
// -----------------------------------------------------------------------------
// A single, reusable row for the Household checklist.
// -----------------------------------------------------------------------------

import type { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { green } from '@mui/material/colors';

import type { Task } from "src/types/snugtotal";

interface Props {
  task: Task;
  onMenu: (id: string, anchor: HTMLElement) => void;   // ← widened
  readOnly: boolean;
}

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string | null) {
  if (!name) {
    return {
      sx: {
        bgcolor: "#FFD700",
        color: "black",
        height: 32,
        width: 32,
      },
      children: "-",
    };
  }

  const initials_array = name.split(" ");

  if (initials_array.length === 1) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 32,
        width: 32,
      },
      children: `${name[0]}${name[1]}`,
    };
  } else {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 32,
        width: 32,
      },
      children: `${initials_array[0][0]}${initials_array[1][0]}`,
    };
  }
}

const ChecklistRow: FC<Props> = ({ task, onMenu, readOnly }) => {
  const navigate = useNavigate();
  const complete = task.status === 'COMPLETE';

  return (
    <ListItem
      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      onClick={() => navigate(task.path)}
    >
      {/* -------------------- avatars ------------------------------------ */}
      <ListItemAvatar sx={{ pr: 2, display: { xs: 'none', sm: 'inherit' } }}>
          <AvatarGroup max={4} sx={{ width: 70, justifyContent: 'center' }}>
            {task.people.map((p) => (
              <Avatar key={p.ud_id} {...stringAvatar(p?.full_name || '')} />
            ))}
          </AvatarGroup>
        </ListItemAvatar>
          {/* -------------------- title + subtitle --------------------------- */}
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" noWrap>
              {task.title}
            </Typography>
          }
          secondary={
            complete ? (
              <Typography variant="caption" color="text.secondary">
                Updated&nbsp;
                {task.updatedAt ? new Date(task.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : null}
              </Typography>
            ) : (
              <>
              {readOnly ?
                <Typography variant="caption" color="text.secondary">
                Not available
              </Typography>
                :
<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <AccessTimeOutlinedIcon fontSize="small" sx={{ color: '#6C737F' }} />
                <Typography variant="caption" color="text.secondary">
                  {task.estMinutes} minutes
                </Typography>
              </Stack>
              }
              </>
            )
          }
          sx={{ pr: 2 }}
        />

      {/* right-hand controls */}
      {complete ? (
        <>
        <Stack direction="row" alignItems="center" sx={{display: { xs: 'none', sm: 'inherit' }}}>
          <Chip
              label="Complete"
              icon={
                <CheckCircleIcon
                  style={{ color: "white" }}
                  fontSize="small"
                />
              }
              style={{
                backgroundColor: green[500],
                color: "white",
                padding: "0 8px",
              }}
              sx={{ width: "100%" }}
            />
            {!readOnly &&
          <IconButton
            size="small"
            color='inherit'
            sx={{ ml: 1 }}
            onClick={(e) => {
              e.stopPropagation();
              onMenu(task.id, e.currentTarget);        // ← now sends anchor
            }}
          >
            <MoreVertIcon />
          </IconButton>
          }
        </Stack>
        <Stack direction="row" alignItems="center" sx={{display: { xs: 'inherit', sm: 'none' }}} spacing={1}>
        <CheckCircleIcon
            style={{ color: green[500] }}
          />
          <SvgIcon>
              <ChevronRightIcon />
            </SvgIcon>
      </Stack>
      </>
      ) : (
        <>
        {!readOnly &&
        <Button
          color="inherit"
          endIcon={
            <SvgIcon>
              <ArrowForwardOutlinedIcon />
            </SvgIcon>
          }
          onClick={(e) => {
            e.stopPropagation();
            navigate(task.path);
          }} 
          sx={{display: { xs: 'none', sm: 'inherit' }}}
        >
          Get started
        </Button>
        }
        <Stack direction="row" alignItems="center" sx={{display: { xs: 'inherit', sm: 'none' }}} spacing={1}>
        
          <SvgIcon>
              <ChevronRightIcon />
            </SvgIcon>
      </Stack>
        </>
      )}

    </ListItem>
  );
};

export default ChecklistRow;